// @flow

import React, { type Node } from 'react';
import 'app/common/react/components/common/ExperimentalLegoGestalt/LegoGestalt.scss';
import cx from 'classnames';
import GestaltColors from 'app/common/react/components/common/ExperimentalLegoGestalt/GestaltColors';
import Weights from 'app/common/react/components/common/ExperimentalLegoGestalt/Weights';

function isRTL(): boolean {
  return Boolean(
    document && document.body && document.body.style && document.body.style.direction === 'rtl',
  );
}

const SIZE_SCALE = {
  xs: '12px',
  sm: '14px',
  md: '16px',
  lg: '18px',
  xl: '21px',
};

type Props = {|
  align?: 'left' | 'right' | 'center' | 'justify',
  children?: Node,
  color?: $Keys<typeof GestaltColors>,
  'data-test-id'?: string,
  inline?: boolean,
  italic?: boolean,
  leading?: 'tall' | 'short',
  overflow?: 'normal' | 'breakWord',
  size?: $Keys<typeof SIZE_SCALE>,
  truncate?: boolean,
  weight?: 'normal' | 'semibold' | 'bold',
  __dangerouslyIncreaseLineHeight?: boolean,
|};

type State = { isRTL: boolean };

export class Text extends React.Component<Props, State> {
  state = { isRTL: false };

  componentDidMount() {
    // Necessary for SSR
    this.setState(() => ({ isRTL: isRTL() }));
  }

  render() {
    const {
      align = 'left',
      children,
      color = 'darkGray',
      'data-test-id': dataTestId,
      inline = false,
      italic = false,
      leading,
      overflow = 'breakWord',
      size = 'md',
      truncate = false,
      weight = 'normal',
      __dangerouslyIncreaseLineHeight = false,
    } = this.props;
    const { isRTL } = this.state;

    const styles = {
      // font size
      fontSize: SIZE_SCALE[size],
      // colors
      color: GestaltColors[color],
      // typography
      fontWeight: Weights[weight],
      ...(leading === 'short' ? { lineHeight: 1.2 } : {}),
      ...(leading === 'tall' || __dangerouslyIncreaseLineHeight ? { lineHeight: 1.5 } : {}),
      ...(align === 'center' ? { textAlign: 'center' } : {}),
      ...(align === 'justify' ? { textAlign: 'justify' } : {}),
      ...(align === 'left' ? { textAlign: isRTL ? 'right' : 'left' } : {}),
      ...(align === 'right' ? { textAlign: isRTL ? 'left' : 'right' } : {}),
      ...(overflow === 'breakWord' ? { hyphens: 'auto', wordWrap: 'break-word' } : {}),
      ...(italic ? { fontStyle: 'italic' } : {}),
      ...(!italic ? { fontStyle: 'normal' } : {}),
    };

    const Tag = inline ? 'span' : 'div';

    return (
      <Tag
        className={cx('LegoGestalt_Font', { LegoGestalt_TextTruncate: truncate })}
        style={styles}
        {...(dataTestId ? { 'data-test-id': dataTestId } : {})}
        {...(truncate && typeof children === 'string' ? { title: children } : {})}
      >
        {children}
      </Tag>
    );
  }
}

export default Text;
